import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Label } from "../ui/label";
import { ExternalLink, Save } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import { motion } from "framer-motion";
import RichTextEditor from "../ui/RichTextEditor";
import PageHeader from "../admin/PageHeader";
import CertificateFileUploader from "../ui/CertificateFileUploader";
import DatePicker from "../ui/DatePicker";
import IndustrySelect from "../ui/IndustrySelect";
import {
  fetchCertificationById,
  editCertification,
} from "@/hooks/useCertification";

const formatDateInput = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

const EditCertification = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    industry: "",
    company: "",
    issueDate: "",
    verifyUrl: "",
    file: null,
  });

  useEffect(() => {
    const loadCertification = async () => {
      try {
        const res = await fetchCertificationById(id);
        const cert = res.certification || res;
        setFormData({
          title: cert.title || "",
          description: cert.description || "",
          industry: cert.industry || "",
          company: cert.company || "",
          issueDate: formatDateInput(cert.issueDate),
          verifyUrl: cert.verifyUrl || "",
          file: cert.file || null,
        });
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to load certification details.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    loadCertification();
  }, [id, toast]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.industry?.trim()) {
      toast({
        title: "Industry required",
        description: "Please select or enter an industry / field.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const fd = new FormData();
      fd.append("title", formData.title);
      fd.append("description", formData.description);
      fd.append("industry", formData.industry.trim());
      fd.append("company", formData.company?.trim() || "");
      fd.append("issueDate", formData.issueDate);
      fd.append("verifyUrl", formData.verifyUrl?.trim() || "");

      if (formData.file instanceof File) {
        fd.append("file", formData.file);
      }

      await editCertification(id, fd);

      toast({
        title: "Certification Updated!",
        description: "Your certification has been successfully updated.",
      });

      navigate("/admin/certifications");
    } catch (error) {
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to update certification.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        Loading certification...
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      <PageHeader
        title="Edit Certification"
        description="Update your certification"
        routeTo="/admin/certifications"
        backTo="Certifications"
      />

      <form onSubmit={handleSubmit}>
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Certification Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="e.g. AWS Solutions Architect Associate"
                    required
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="industry">Industry / Field *</Label>
                    <IndustrySelect
                      id="industry"
                      value={formData.industry}
                      onChange={(industry) =>
                        setFormData((prev) => ({ ...prev, industry }))
                      }
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company">Company / Issuer (Optional)</Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="e.g. AWS, Coursera, Karachi AI"
                    />
                    <p className="text-xs text-muted-foreground">
                      Where you earned this certification
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="issueDate">Issue Date *</Label>
                  <DatePicker
                    id="issueDate"
                    value={formData.issueDate}
                    onChange={(issueDate) =>
                      setFormData((prev) => ({ ...prev, issueDate }))
                    }
                    placeholder="Select issue date"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="verifyUrl">Verify URL (Optional)</Label>
                  <div className="relative">
                    <ExternalLink className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                    <Input
                      id="verifyUrl"
                      name="verifyUrl"
                      value={formData.verifyUrl}
                      onChange={handleChange}
                      placeholder="https://www.credly.com/badges/..."
                      className="pl-10"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Leave empty to hide the Verify button on the public page.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Description *</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Describe the certification using the rich text editor
                </p>
              </CardHeader>
              <CardContent>
                <RichTextEditor
                  key={id}
                  value={formData.description}
                  onChange={(description) =>
                    setFormData((prev) => ({ ...prev, description }))
                  }
                  placeholder="Write about this certification..."
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Certificate File</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Upload a new PNG, JPG, or PDF to replace the current file
                </p>
              </CardHeader>
              <div className="pl-6 pb-6">
                <CertificateFileUploader
                  value={formData.file}
                  onChange={(file) =>
                    setFormData((prev) => ({ ...prev, file }))
                  }
                />
              </div>
            </Card>
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  type="submit"
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                      className="w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"
                    />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {isSubmitting ? "Updating..." : "Update Certification"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => navigate("/admin/certifications")}
                >
                  Cancel
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </motion.div>
  );
};

export default EditCertification;
